import {
  Button,
  MenuItem,
  Modal,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  ModalFooter,
  useToast,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';

import {
  ShareFundingComponentFragmentGQLType,
  ResolvedLoanFragmentGQLType,
  PrincipalPaymentMetricsGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  errorToastAdminConfig,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useGetPrincipalPaymentMetricsByLoanIdQuery } from '~/packages/invoices/requests/invoices.gql.generated';
import { GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType } from '~/packages/loans/requests/ibsIntegration.gql.generated';
import {
  useMarkLenderShareComponentFundTransferDateMutation,
  GetResolvedLoanByIdDocumentGQLType,
} from '~/packages/loans/requests/loans.gql.generated';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
  GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
} from '~/packages/transactions/requests/transactions.gql.generated';

interface UndoLenderShareComponentFundedModalProps {
  fundingComponent: ShareFundingComponentFragmentGQLType;
  modalControls: ReturnType<typeof useDisclosure>;
}

const UndoLenderShareComponentFundedModal = ({
  fundingComponent,
  modalControls,
}: UndoLenderShareComponentFundedModalProps) => {
  const toast = useToast();

  const [
    markLenderShareComponentFundTransferDate,
    { loading: isLoading },
  ] = useMarkLenderShareComponentFundTransferDateMutation({
    refetchQueries: [
      GetResolvedLoanByIdDocumentGQLType,
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
      GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
      GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType,
    ],
  });

  const undoMarkFunded = async (): Promise<void> => {
    const { fundingRequestComponentId, lenderShareId } = fundingComponent;

    try {
      await markLenderShareComponentFundTransferDate({
        variables: {
          lenderShareId,
          fundingRequestComponentId,
        },
      });
      toast({
        ...successToastConfig,
        title: `Successfully unmarked Lender share fund transfer date.`,
      });
    } catch (error) {
      toast({
        ...errorToastAdminConfig,
        duration: 6000,
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'If you are unable to resolve the error, contact the Finley support team for assistance.',
        }),
      });
    }
  };

  return (
    <Modal {...modalControls}>
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>
          <Typography variant="section-subtitle">Undo Mark Funded</Typography>
          <Typography variant="base-muted">
            Undoing &quot;Mark Funded&quot; will remove the current Lender
            Funded Date and may impact accounting records. Ensure accuracy
            before proceeding.
          </Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalFooter gap="0.5rem" mt="1rem">
          <Button
            colorScheme="v2-aurora"
            onClick={modalControls.onClose}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={async () => {
              await undoMarkFunded();
              modalControls.onClose();
            }}
            isLoading={isLoading}
          >
            Apply Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const calculateIsDisabled = (params: {
  fundingComponent: ShareFundingComponentFragmentGQLType;
  resolvedLoan: ResolvedLoanFragmentGQLType;
  principalPaymentMetrics: PrincipalPaymentMetricsGQLType;
}): { isDisabled: boolean; disabledMessage: string } => {
  const { fundingComponent, resolvedLoan, principalPaymentMetrics } = params;

  if (!fundingComponent.fundTransferDate) {
    return { isDisabled: true, disabledMessage: 'Not marked as funded yet' };
  }

  if (
    principalPaymentMetrics.latestPaymentDate &&
    principalPaymentMetrics.latestPaymentDate >=
      fundingComponent.fundTransferDate
  ) {
    return {
      isDisabled: true,
      disabledMessage:
        'Cannot undo fund transfer as principal payment has been made on or afterwards',
    };
  }

  const isAdminAgent =
    resolvedLoan.parties.adminAgentOrganization?.id ===
    fundingComponent.lenderOrganizationId;
  if (isAdminAgent) {
    return {
      isDisabled: true,
      disabledMessage: 'Admin Agent cannot undo fund transfer',
    };
  }

  return { isDisabled: false, disabledMessage: '' };
};

interface UndoLenderShareComponentFundedMenuItemProps {
  fundingComponent: ShareFundingComponentFragmentGQLType;
  resolvedLoan: ResolvedLoanFragmentGQLType;
}

export const UndoLenderShareComponentFundedMenuItem = ({
  fundingComponent,
  resolvedLoan,
}: UndoLenderShareComponentFundedMenuItemProps) => {
  const modalControls = useDisclosure();

  const principalPaymentMetricsQuery = useGetPrincipalPaymentMetricsByLoanIdQuery(
    {
      fetchPolicy: 'cache-and-network',
      variables: { loanId: resolvedLoan.id },
    },
  );
  if (principalPaymentMetricsQuery.error) {
    throw principalPaymentMetricsQuery.error;
  }

  if (
    principalPaymentMetricsQuery.loading ||
    !principalPaymentMetricsQuery.data
  ) {
    return <Skeleton height="full" width="full" />;
  }

  const principalPaymentMetrics = principalPaymentMetricsQuery.data.result;
  const { isDisabled, disabledMessage } = calculateIsDisabled({
    fundingComponent,
    resolvedLoan,
    principalPaymentMetrics,
  });

  return (
    <MenuItem as="div">
      <Tooltip label={disabledMessage} isDisabled={!isDisabled}>
        <Button
          variant="ghost"
          colorScheme="gray"
          fontWeight={400}
          onClick={modalControls.onOpen}
          isDisabled={isDisabled}
        >
          Undo Mark Funded
        </Button>
      </Tooltip>
      <UndoLenderShareComponentFundedModal
        fundingComponent={fundingComponent}
        modalControls={modalControls}
      />
    </MenuItem>
  );
};
